/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license		MIT
 * @description Frontend component providing a context menu with shortcuts to various sections of the app.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Trophy,
  Package,
  Book,
  Swords,
  MessageSquare,
  LogOut,
  XCircle,
} from "lucide-react";

export default function ContextMenu({ onAction }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setVisible(true);
    setPosition({ x: e.pageX, y: e.pageY });
  }, []);

  const handleClick = useCallback(() => setVisible(false), []);

  useEffect(() => {
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("click", handleClick);
    };
  }, [handleContextMenu, handleClick]);

  if (!visible) return null;

  const handleItemClick = (action) => {
    if (action === "LOGOUT") {
      if (
        !window.confirm(
          "ARE YOU SURE YOU WANT TO HEAD TO THE SAFE HOUSE? (LOGOUT)"
        )
      ) {
        return;
      }
    }
    onAction(action);
    setVisible(false);
  };

  return (
    <div
      className="fixed z-[10000] bg-zinc-950 border-2 border-green-900 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] w-48 font-mono animate-in fade-in zoom-in duration-100"
      style={{ top: position.y, left: position.x }}
    >
      <div className="bg-green-900/20 p-2 border-b border-green-900 text-[10px] text-green-500 font-black italic uppercase">
        Shortcuts
      </div>
      <ul className="py-1">
        <li onClick={() => handleItemClick("LEADERBOARD")} className="ctx-item">
          <Trophy size={14} className="text-yellow-500" /> Leaderboard
        </li>
        <li
          onClick={() => handleItemClick("INVENTORY")}
          className="ctx-item border-t border-zinc-900"
        >
          <Package size={14} className="text-blue-500" /> Inventory
        </li>
        <li
          onClick={() => handleItemClick("FIELD_MANUAL")}
          className="ctx-item border-t border-zinc-900"
        >
          <Book size={14} className="text-green-500" /> Field Manual
        </li>
        <li
          onClick={() => handleItemClick("FIGHT_LOGS")}
          className="ctx-item border-t border-zinc-900"
        >
          <Swords size={14} className="text-red-500" /> Fight Logs
        </li>
        <li
          onClick={() => handleItemClick("SHOUTBOX")}
          className="ctx-item border-t border-zinc-900"
        >
          <MessageSquare size={14} className="text-zinc-400" /> Shoutbox
        </li>
        <li
          onClick={() => handleItemClick("LOGOUT")}
          className="ctx-item border-t border-zinc-900 text-red-500 hover:bg-red-600 hover:text-black"
        >
          <LogOut size={14} /> Logout
        </li>
        <li
          onClick={() => setVisible(false)}
          className="ctx-item border-t border-zinc-900 text-zinc-600"
        >
          <XCircle size={12} /> Close Menu
        </li>
      </ul>

      <style>
        {`
          .ctx-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 0.75rem;
            font-size: 0.75rem;
            color: #a1a1aa; /* zinc-400 */
            text-transform: uppercase;
            font-style: italic;
            font-weight: 700;
            cursor: pointer;
            transition: background-color 0.2s, color 0.2s;
          }
          .ctx-item:hover {
            background-color: #16a34a; /* green-600 */
            color: #000;
          }
        `}
      </style>
    </div>
  );
}
